import { RINK, type GoalTag, type PhysicsEvent, type Role } from '../types';
import { defaultPhysicsConfig, type GameState, type Phase, type PhysicsConfig } from './physics';
import { triggerGoalBob } from './hover';

/** Accent color for a goal-tag's extra particle burst. Null = no extra
 *  burst (regular goal). Kept hard-coded rather than palette-derived so
 *  each tag retains its semantic color across palettes. */
function goalTagColor(tag: GoalTag | undefined): string | null {
  switch (tag) {
    case 'ace':         return '#ffd866'; // gold
    case 'banger':      return '#ffffff'; // extra-bright white
    case 'long':        return '#5eaaff'; // sky blue
    case 'bank':        return '#b78aff'; // violet
    case 'own':         return '#ff5a3c'; // red-orange (rueful)
    case 'hat-trick':   return '#ff66ff'; // hot magenta (showpiece)
    case 'snipe':       return '#ff3838'; // laser red
    case 'dunk':        return '#ff7733'; // punchy orange
    case 'top-shelf':   return '#66e8ff'; // ice blue
    case 'shutout':     return '#ff2444'; // blood red (savage)
    case 'comeback':    return '#ff8e40'; // ember orange
    case 'speedrun':    return '#ffe44f'; // lightning yellow
    case 'toilet':      return '#9c8a64'; // muddy tan (loser color)
    default:            return null;
  }
}

/** Per-frame current match config — set by tickScene(). Read by paddle/puck
 *  drawing helpers so Big Paddle and friends actually grow the visual.
 *  Exported via getCurrentConfig() for render-gl.ts to share. */
let currentConfig: PhysicsConfig = defaultPhysicsConfig();
export function getCurrentConfig(): PhysicsConfig { return currentConfig; }

/** Per-frame puck speed, used by both renderers to scale the puck's
 *  drawn halo so fast pucks visibly radiate more. Set in tickScene. */
let currentPuckSpeed = 0;
export function getCurrentPuckSpeed(): number { return currentPuckSpeed; }

// Color slots — populated by setPaletteColors() at session start.
// Initial values are the Synthwave defaults; if applyRenderPalette isn't
// called these still produce a sensible look.
const COLORS = {
  bg: '#08020e',
  surface: '#150726',
  surfaceFar: '#0d041a',
  rim: '#ff2bd6',
  rimGlow: '#ff2bd6aa',
  line: '#22e7ff',
  lineGlow: '#22e7ff88',
  hostPaddle: '#ff2bd6',
  guestPaddle: '#22e7ff',
  puck: '#fff7a8',
  puckGlow: '#fff7a8cc',
  text: '#f5f3ff',
  shadow: '#00000080',
  capHighlight: '#ffffff22',
};

/** Push the four palette colors into the renderer's COLOR slots, plus
 *  recompute derived glows. Call once on load. Invalidates the static
 *  rink cache so it gets rebuilt with the new rim color. */
export function applyRenderPalette(host: string, guest: string, puck: string, bg: string): void {
  COLORS.bg = bg;
  COLORS.rim = host;
  COLORS.rimGlow = host + 'aa';
  COLORS.line = guest;
  COLORS.lineGlow = guest + '88';
  COLORS.hostPaddle = host;
  COLORS.guestPaddle = guest;
  COLORS.puck = puck;
  COLORS.puckGlow = puck + 'cc';
  // Force rebuild of cached layers tied to color.
  staticCache = null;
  staticCacheKey = '';
  entitySprites = null;
}

/**
 * Vertical/lateral aspect ratio for entities on the table — i.e. how
 * squashed a puck looks when viewed at this camera angle. A true
 * perspective projection of a circle on a flat plane has a CONSTANT
 * aspect ratio across the whole plane (= cos(tilt)). Picking ~0.55
 * approximates "standing at the end of the table, looking down it."
 *
 * Position is still projected per-depth (entities shrink as they recede)
 * — only the aspect ratio is fixed.
 */
const ENTITY_FORESHORTEN = 1.0;

/**
 * Linear-trapezoid perspective. Cheap, looks plausible for a table seen
 * from one end. Each player's own end is the bottom of the screen, the
 * opponent's end tapers off near the top. World coordinates are unchanged
 * — the camera is the only thing that varies per role.
 */
interface View {
  canvasW: number;
  canvasH: number;
  /** Screen Y for the player's end of the rink. */
  nearY: number;
  /** Screen Y for the opponent's end. */
  farY: number;
  /** Half-width of the rink on screen at the player's end. */
  nearHalfWidth: number;
  /** Half-width on screen at the opponent's end. */
  farHalfWidth: number;
  role: Role;
  // ── 1-point perspective parameters ──────────────────────────────────────
  // screen_y(depth) = horizonY + screenYScale / (depth + a)
  // halfHere(depth) = nearHalfWidth * a / (depth + a)
  // `a` is derived from the near/far half-width ratio so the rink visual
  // still occupies [nearY, farY] vertically and [-nearHalfWidth, +nearHalfWidth]
  // at the near edge. The horizon (vanishing point) lives off-screen above
  // the rink — found analytically from the two anchors.
  a: number;
  horizonY: number;
  screenYScale: number;
}

interface Projection {
  /** Screen-space (canvas px) position. */
  x: number;
  y: number;
  /** 0 at the player's end, 1 at the opponent's end. */
  depthT: number;
  /** Screen px per 1 world px laterally at this depth. */
  latScale: number;
  /** Screen px per 1 world px along the depth axis. */
  depthScale: number;
}

/** Max CSS-pixel width that the rink occupies on screen. The CANVAS may
 *  be wider than this (stars fill the full viewport), but the rink's
 *  lateral spread is bounded by this value so playable area doesn't
 *  balloon on large monitors. */
const RINK_MAX_CSS_PX = 1100;

function computeView(canvas: HTMLCanvasElement, role: Role): View {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  // Rink visual width on screen is min(full canvas, RINK_MAX_CSS_PX).
  // Centered horizontally — canvas.width/2 stays the rink center.
  const rinkW = Math.min(canvas.width, RINK_MAX_CSS_PX * dpr);
  const nearY = canvas.height * 0.93;
  const farY = canvas.height * 0.18;
  const nearHalfWidth = rinkW * 0.46;
  const farHalfWidth = rinkW * 0.16;
  // True 1-point perspective parameters. The shrink ratio of nearHalfWidth
  // → farHalfWidth across the rink depth implicitly fixes the camera
  // distance `a` behind the near edge. Derivation in interface comment.
  const a = RINK.width * farHalfWidth / (nearHalfWidth - farHalfWidth);
  const dN = (nearY - farY) * (RINK.width + a) / RINK.width;
  const horizonY = nearY - dN;
  const screenYScale = a * dN;
  return {
    canvasW: canvas.width,
    canvasH: canvas.height,
    nearY, farY, nearHalfWidth, farHalfWidth,
    role,
    a, horizonY, screenYScale,
  };
}

function project(wx: number, wy: number, v: View): Projection {
  // Host views from wx=0 toward wx=W; guest from wx=W toward wx=0.
  // Convert to depth-from-camera (0 at near, RINK.width at far).
  const depth = v.role === 'host' ? wx : RINK.width - wx;
  const invZ = 1 / (depth + v.a);
  const screenY = v.horizonY + v.screenYScale * invZ;
  const halfHere = v.nearHalfWidth * v.a * invZ;
  // Guest's lateral axis flips (their right hand = world's -y side).
  const wyAdj = v.role === 'host' ? wy : RINK.height - wy;
  const lateralT = (wyAdj - RINK.height / 2) / (RINK.height / 2); // -1..1
  const screenX = v.canvasW / 2 + lateralT * halfHere;
  const latScale = halfHere / (RINK.height / 2);
  // Depth scale: |d(screenY)/d(wx)| at this depth.
  // d/d(depth) of (horizon + scale/(depth+a)) = -scale/(depth+a)^2.
  const depthScale = v.screenYScale * invZ * invZ;
  const depthT = depth / RINK.width;
  return { x: screenX, y: screenY, depthT, latScale, depthScale };
}

function unproject(sx: number, sy: number, v: View): { x: number; y: number } {
  // Invert the perspective: solve screenY = horizonY + screenYScale/(depth+a)
  // for depth. Outside the visible rink we'd get NaN/negative — clamp.
  const dy = sy - v.horizonY;
  // dy must be in the same sign as screenYScale (both positive for our
  // setup where the rink lives below the horizon). Guard against zero.
  let depth: number;
  if (dy <= 0) {
    depth = RINK.width;
  } else {
    depth = v.screenYScale / dy - v.a;
    if (depth < 0) depth = 0;
    if (depth > RINK.width) depth = RINK.width;
  }
  const invZ = 1 / (depth + v.a);
  const halfHere = v.nearHalfWidth * v.a * invZ;
  const lateralT = (sx - v.canvasW / 2) / halfHere;
  const wyAdj = RINK.height / 2 + lateralT * (RINK.height / 2);
  const wy = v.role === 'host' ? wyAdj : RINK.height - wyAdj;
  const wx = v.role === 'host' ? depth : RINK.width - depth;
  return { x: wx, y: wy };
}

export function fitCanvas(canvas: HTMLCanvasElement): void {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
}

export interface RenderState {
  state: GameState;
  role: Role;
}

/**
 * Static-layer cache. The rink surface + center line + center circle +
 * rim + goal mouths only change when the canvas resizes or the role
 * flips. Drawing them involves ~6 shadowBlur passes — way too expensive
 * at 60Hz. We render them once into an offscreen canvas keyed on
 * (width, height, role) and blit each frame.
 */
let staticCache: HTMLCanvasElement | null = null;
let staticCacheKey = '';

/**
 * Pre-build the static rink layer. Call once after `fitCanvas` and once
 * after each resize — keeps the first RAF frame from paying the
 * shadowBlur tax for the rim/lines/surface.
 */
export function warmStaticCache(canvas: HTMLCanvasElement, role: Role): void {
  ensureStaticLayer(canvas, role);
}

/**
 * Expose the pre-rendered static layer so the WebGL renderer can upload
 * it as a texture (reuses all the rim/lines/pocket drawing without
 * porting it to WebGL geometry).
 */
export function getStaticLayer(canvas: HTMLCanvasElement, role: Role): HTMLCanvasElement {
  return ensureStaticLayer(canvas, role);
}

/** Force the static rink cache to rebuild on next access — call after
 *  changes that affect rim/pocket geometry (e.g., mid-match modifier
 *  change). The cache key also includes goalHalfHeight + pocketDepth
 *  so it auto-invalidates across configs, but new matches still need
 *  this for first-frame correctness. */
export function invalidateStaticCache(): void {
  staticCache = null;
  staticCacheKey = '';
}

function ensureStaticLayer(canvas: HTMLCanvasElement, role: Role): HTMLCanvasElement {
  // Key includes config dims that change rim/pocket shape so different
  // modifiers can coexist without stomping on each other's cache.
  const key = `${canvas.width}x${canvas.height}:${role}:${currentConfig.goalHalfHeight}:${currentConfig.pocketDepth}`;
  if (staticCache && staticCacheKey === key) return staticCache;
  const off = document.createElement('canvas');
  off.width = canvas.width;
  off.height = canvas.height;
  const cctx = off.getContext('2d');
  if (!cctx) throw new Error('failed to acquire offscreen 2d context');
  const view = computeView(canvas, role);
  // No bg fill — leaving the canvas transparent means the WebGL
  // renderer can place a starfield BEHIND the rink (the texture has
  // alpha=0 outside the rim/lines/pockets). The Canvas-2D renderer
  // fills its own bg before drawImage'ing this cache.
  drawCenterLine(cctx, view);
  drawCenterCircle(cctx, view);
  drawRim(cctx, view);
  staticCache = off;
  staticCacheKey = key;
  return off;
}

// ─── Entity sprite cache ──────────────────────────────────────────────────
// Paddles and puck are pre-rendered ONCE into offscreen canvases, with
// shadowBlur applied at sprite creation. Each frame we just drawImage
// the sprite scaled to the target size — fast AND keeps the soft glow.
const SPRITE_SIZE = 128;
const SPRITE_BODY_R = 36;
/** Scale from world body radius to screen-space drawSize for the full
 * sprite (body + halo). Body covers SPRITE_BODY_R within a SPRITE_SIZE
 * canvas, so drawing the sprite at `2 * bodyRadius * RATIO` makes the
 * body land at the desired bodyRadius and leaves room for the halo. */
const SPRITE_DRAW_RATIO = SPRITE_SIZE / (2 * SPRITE_BODY_R);

interface EntitySprites {
  host: HTMLCanvasElement;
  guest: HTMLCanvasElement;
  puck: HTMLCanvasElement;
}
let entitySprites: EntitySprites | null = null;

function makePaddleSprite(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = SPRITE_SIZE;
  c.height = SPRITE_SIZE;
  const cx = c.getContext('2d');
  if (!cx) throw new Error('sprite ctx');
  // Soft glow halo via shadowBlur (paid once, then cached forever).
  cx.shadowColor = color;
  cx.shadowBlur = 26;
  cx.fillStyle = color;
  cx.beginPath();
  cx.arc(SPRITE_SIZE / 2, SPRITE_SIZE / 2, SPRITE_BODY_R, 0, Math.PI * 2);
  cx.fill();
  // Cap highlight (no shadow).
  cx.shadowBlur = 0;
  cx.shadowColor = 'transparent';
  cx.fillStyle = '#ffffff44';
  cx.beginPath();
  cx.arc(
    SPRITE_SIZE / 2,
    SPRITE_SIZE / 2 - SPRITE_BODY_R * 0.3,
    SPRITE_BODY_R * 0.55,
    0, Math.PI * 2,
  );
  cx.fill();
  return c;
}

function makePuckSprite(color: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = SPRITE_SIZE;
  c.height = SPRITE_SIZE;
  const cx = c.getContext('2d');
  if (!cx) throw new Error('sprite ctx');
  cx.shadowColor = color;
  cx.shadowBlur = 32; // slightly bigger bloom on the puck
  cx.fillStyle = color;
  cx.beginPath();
  cx.arc(SPRITE_SIZE / 2, SPRITE_SIZE / 2, SPRITE_BODY_R, 0, Math.PI * 2);
  cx.fill();
  return c;
}

function ensureEntitySprites(): EntitySprites {
  if (!entitySprites) {
    entitySprites = {
      host: makePaddleSprite(COLORS.hostPaddle),
      guest: makePaddleSprite(COLORS.guestPaddle),
      puck: makePuckSprite(COLORS.puck),
    };
  }
  return entitySprites;
}

// Render entities with STABLE identity. The previous version sorted
// `ents[]` in place by depth and then on the next frame wrote
// host/guest/puck data back into ents[0], ents[1], ents[2] — but those
// slots may now hold different entity objects than they did when last
// populated. The result was a puck rendered at a paddle's position
// (and vice versa) whenever entities crossed in depth, which read
// visually as flicker/strobing on fast motion.
//
// Fix: keep each entity in its own named reference. Compute depths,
// sort three locals into back-to-front order, draw in that order.
type RenderEntity =
  | { kind: 'paddle'; wx: number; wy: number; color: string; depthT: number }
  | { kind: 'puck'; wx: number; wy: number; depthT: number };

const hostPaddleEnt: RenderEntity = { kind: 'paddle', wx: 0, wy: 0, color: COLORS.hostPaddle, depthT: 0 };
const guestPaddleEnt: RenderEntity = { kind: 'paddle', wx: 0, wy: 0, color: COLORS.guestPaddle, depthT: 0 };
const puckEnt: RenderEntity = { kind: 'puck', wx: 0, wy: 0, depthT: 0 };

function drawEntity(ctx: CanvasRenderingContext2D, e: RenderEntity, v: View, now: number): void {
  if (e.kind === 'paddle') {
    const which = e.color === COLORS.hostPaddle ? 'host' : 'guest';
    drawPaddle(ctx, e.wx, e.wy, e.color, v, hitPulse(which, now), now);
  } else {
    drawPuck(ctx, e.wx, e.wy, v, now);
  }
}

// ─── Animation state ──────────────────────────────────────────────────────
// All animation state is module-level. The render loop reads it; physics
// and net code never touch it. To trigger effects from physics events,
// call applyEvents() with the event list and a `now` timestamp.

export type Effect =
  | { kind: 'impact'; t0: number; duration: number; wx: number; wy: number; color: string }
  | { kind: 'wallflash'; t0: number; duration: number; wx: number; wy: number }
  | { kind: 'goal'; t0: number; duration: number; scorer: 'host' | 'guest' }
  | { kind: 'particle'; t0: number; duration: number; wx: number; wy: number; vx: number; vy: number; color: string; size: number };

const effects: Effect[] = [];

/** Snapshot of active effects (read-only) for the WebGL renderer. */
export function getEffectsSnapshot(): readonly Effect[] {
  return effects;
}

/** Current shake offset for the WebGL renderer to apply as a uniform. */
export function getShakeOffset(now: number): { dx: number; dy: number } {
  return computeShake(now);
}

// Streaming sparks: each tracked entity emits a particle every
// `spawnDist` world units of travel. Frame-rate-independent and
// packet-rate-independent. One emitter per entity.
interface SparkEmitter {
  prevX: number;
  prevY: number;
  seen: boolean;
  acc: number;
}
const puckEmitter: SparkEmitter = { prevX: 0, prevY: 0, seen: false, acc: 0 };
const puckGlowEmitter: SparkEmitter = { prevX: 0, prevY: 0, seen: false, acc: 0 };
const puckHaloEmitter: SparkEmitter = { prevX: 0, prevY: 0, seen: false, acc: 0 };
const hostEmitter: SparkEmitter = { prevX: 0, prevY: 0, seen: false, acc: 0 };
const guestEmitter: SparkEmitter = { prevX: 0, prevY: 0, seen: false, acc: 0 };

function resetEmitters(): void {
  puckEmitter.seen = false;
  puckGlowEmitter.seen = false;
  puckHaloEmitter.seen = false;
  hostEmitter.seen = false;
  guestEmitter.seen = false;
  puckEmitter.acc = 0;
  puckGlowEmitter.acc = 0;
  puckHaloEmitter.acc = 0;
  hostEmitter.acc = 0;
  guestEmitter.acc = 0;
}

let shakeEndAt = 0;
let shakeMag = 0;

let lastHostHitAt = -1000;
let lastGuestHitAt = -1000;

let lastPlayPhase: Phase = 'play';
let lastServeStart = 0;

/** Geometry-Wars-style grid force. Each force pushes nearby grid points
 * outward in a wave that decays with distance and time. */
export interface GridForce {
  wx: number;
  wy: number;
  t0: number;
  duration: number;
  /** Peak displacement in world units near the impact point. */
  magnitude: number;
  /** World-unit radius over which the force reaches zero. */
  radius: number;
}
const gridForces: GridForce[] = [];

/** Read-only snapshot of active grid forces — used by the WebGL renderer
 *  to upload them as uniforms each frame. */
export function getGridForceSnapshot(): readonly GridForce[] {
  return gridForces;
}

function spawnGridForce(wx: number, wy: number, magnitude: number, radius: number, duration: number, now: number): void {
  gridForces.push({ wx, wy, t0: now, duration, magnitude, radius });
}

function tickGridForces(now: number): void {
  let i = 0;
  while (i < gridForces.length) {
    if (now >= gridForces[i]!.t0 + gridForces[i]!.duration) gridForces.splice(i, 1);
    else i++;
  }
}

/**
 * Per-point displacement under all active forces. Each force pushes the
 * point radially outward with a decaying oscillation (so the wave
 * "passes through" rather than just bulging out and back).
 */
function gridDisplacement(wx: number, wy: number, now: number, out: { dx: number; dy: number }): void {
  out.dx = 0;
  out.dy = 0;
  for (const f of gridForces) {
    const age = (now - f.t0) / f.duration;
    if (age < 0 || age >= 1) continue;
    const ddx = wx - f.wx;
    const ddy = wy - f.wy;
    const dist = Math.sqrt(ddx * ddx + ddy * ddy);
    if (dist < 0.001 || dist > f.radius) continue;
    const distFalloff = 1 - dist / f.radius;
    // Wave traveling outward. Phase = distance scaled, modulated by age.
    const wave = Math.cos(dist / 24 - age * Math.PI * 3);
    const ageFalloff = (1 - age) * (1 - age);
    const force = f.magnitude * distFalloff * ageFalloff * wave;
    out.dx += (ddx / dist) * force;
    out.dy += (ddy / dist) * force;
  }
}

/** Translate physics events into spawned effects + side-channel state. */
export function applyEvents(evs: readonly PhysicsEvent[], now: number): void {
  for (const ev of evs) {
    if (ev.e === 'hit') {
      const color = ev.paddle === 'host' ? COLORS.hostPaddle : COLORS.guestPaddle;
      effects.push({ kind: 'impact', t0: now, duration: 280, wx: ev.x, wy: ev.y, color });
      if (ev.paddle === 'host') lastHostHitAt = now;
      else lastGuestHitAt = now;
      spawnGridForce(ev.x, ev.y, 14 + Math.min(ev.speed * 0.6, 16), 220, 480, now);
      // Burst of paddle-colored sparks radiating omnidirectionally from
      // the hit point. Count scales with collision speed.
      const hitCount = 16 + Math.min(14, Math.floor(ev.speed * 0.8));
      for (let i = 0; i < hitCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const spd = 2.5 + Math.random() * 4;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 280 + Math.random() * 260,
          wx: ev.x,
          wy: ev.y,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd,
          color,
          size: 1.2 + Math.random() * 1.4,
        });
      }
    } else if (ev.e === 'wall') {
      effects.push({ kind: 'wallflash', t0: now, duration: 220, wx: ev.x, wy: ev.y });
      spawnGridForce(ev.x, ev.y, 8 + Math.min(ev.speed * 0.4, 10), 160, 380, now);
      // Sparks flying inward off the wall. Direction = inward normal.
      const nx = ev.horizontal ? 0 : (ev.x < RINK.width / 2 ? 1 : -1);
      const ny = ev.horizontal ? (ev.y < RINK.height / 2 ? 1 : -1) : 0;
      const wallCount = 10 + Math.min(10, Math.floor(ev.speed * 0.7));
      for (let i = 0; i < wallCount; i++) {
        // Half-disc fanned in the inward direction.
        const baseAngle = Math.atan2(ny, nx);
        const angle = baseAngle + (Math.random() - 0.5) * Math.PI * 0.8;
        const spd = 2 + Math.random() * 4;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 240 + Math.random() * 200,
          wx: ev.x,
          wy: ev.y,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd,
          color: '#fff7a8',
          size: 1.0 + Math.random() * 1.0,
        });
      }
    } else if (ev.e === 'goal') {
      effects.push({ kind: 'goal', t0: now, duration: 900, scorer: ev.scorer });
      shakeEndAt = now + 460;
      shakeMag = 22;
      triggerGoalBob(now);
      const goalX = ev.scorer === 'host' ? RINK.width : 0;
      // Big slow wave through the whole table from the conceded pocket.
      spawnGridForce(goalX, RINK.height / 2, 44, 800, 1100, now);
      // Strong localized ripple at the puck's impact point on the goal mouth.
      spawnGridForce(ev.x, ev.y, 30, 360, 700, now);
      // PUCK EXPLOSION at impact point — radial white burst reads as the
      // puck shattering against the goal.
      for (let i = 0; i < 70; i++) {
        const aa = Math.random() * Math.PI * 2;
        const spd = 3 + Math.random() * 7;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 500 + Math.random() * 450,
          wx: ev.x, wy: ev.y,
          vx: Math.cos(aa) * spd,
          vy: Math.sin(aa) * spd,
          color: '#ffffff',
          size: 1.5 + Math.random() * 2.2,
        });
      }
      // Massive particle eruption — scorer color, hemispherical outward.
      const dirSign = ev.scorer === 'host' ? 1 : -1;
      const cy = RINK.height / 2;
      const color = ev.scorer === 'host' ? COLORS.hostPaddle : COLORS.guestPaddle;
      for (let i = 0; i < 90; i++) {
        const angle = (Math.random() - 0.5) * Math.PI * 1.0;
        const speed = 3 + Math.random() * 7;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 700 + Math.random() * 600,
          wx: goalX,
          wy: cy + (Math.random() - 0.5) * RINK.goalHalfHeight * 1.6,
          vx: dirSign * speed * Math.cos(angle),
          vy: speed * Math.sin(angle),
          color,
          size: 2 + Math.random() * 4,
        });
      }
      // Plus a smaller white spray as "debris" for variety.
      for (let i = 0; i < 30; i++) {
        const angle = (Math.random() - 0.5) * Math.PI * 1.2;
        const speed = 2 + Math.random() * 5;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 500 + Math.random() * 500,
          wx: goalX,
          wy: cy + (Math.random() - 0.5) * RINK.goalHalfHeight * 1.8,
          vx: dirSign * speed * Math.cos(angle),
          vy: speed * Math.sin(angle),
          color: '#ffffff',
          size: 1.2 + Math.random() * 2,
        });
      }
      // Tag-specific accent burst: distinctive color + count so each
      // flair goal reads differently from a regular one.
      const tagColor = goalTagColor(ev.tag);
      if (tagColor) {
        // Showpiece tags get extra particles; the rest are 40. Toilet
        // goals get fewer — they're meant to be sad/anticlimactic.
        const accentCount =
          ev.tag === 'hat-trick' ? 100 :
          ev.tag === 'shutout' ? 90 :
          ev.tag === 'banger' ? 70 :
          ev.tag === 'comeback' ? 70 :
          ev.tag === 'toilet' ? 15 :
          40;
        for (let i = 0; i < accentCount; i++) {
          const angle = (Math.random() - 0.5) * Math.PI * 1.4;
          const speed = 4 + Math.random() * 8;
          effects.push({
            kind: 'particle',
            t0: now,
            duration: 900 + Math.random() * 700,
            wx: goalX,
            wy: cy + (Math.random() - 0.5) * RINK.goalHalfHeight * 2.0,
            vx: dirSign * speed * Math.cos(angle),
            vy: speed * Math.sin(angle),
            color: tagColor,
            size: 2.5 + Math.random() * 2.5,
          });
        }
        // Banger gets an extra screen shake on top of the base goal shake.
        if (ev.tag === 'banger') {
          shakeEndAt = now + 620;
          shakeMag = 30;
        }
      }
    } else if (ev.e === 'reset') {
      // Stalemate-breaker teleport: explosion at the old position,
      // bright spawn-in flash at the new (center) position, screen shake,
      // grid shock at both endpoints.
      shakeEndAt = now + 240;
      shakeMag = 10;
      spawnGridForce(ev.fromX, ev.fromY, 28, 380, 700, now);
      spawnGridForce(ev.toX, ev.toY, 22, 320, 600, now);
      // Big white burst at the old (stuck) position.
      for (let i = 0; i < 50; i++) {
        const a = Math.random() * Math.PI * 2;
        const spd = 4 + Math.random() * 6;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 500 + Math.random() * 400,
          wx: ev.fromX, wy: ev.fromY,
          vx: Math.cos(a) * spd,
          vy: Math.sin(a) * spd,
          color: '#ffffff',
          size: 1.2 + Math.random() * 1.6,
        });
      }
      // Smaller yellow burst at the spawn (center) point.
      for (let i = 0; i < 24; i++) {
        const a = Math.random() * Math.PI * 2;
        const spd = 2 + Math.random() * 4;
        effects.push({
          kind: 'particle',
          t0: now,
          duration: 400 + Math.random() * 300,
          wx: ev.toX, wy: ev.toY,
          vx: Math.cos(a) * spd,
          vy: Math.sin(a) * spd,
          color: '#fff7a8',
          size: 1.0 + Math.random() * 1.2,
        });
      }
    }
  }
}

function tickEffects(now: number): void {
  let i = 0;
  while (i < effects.length) {
    if (now >= effects[i]!.t0 + effects[i]!.duration) effects.splice(i, 1);
    else i++;
  }
}

/**
 * Emit streak sparks from any moving entity. Spawn rate is proportional
 * to distance traveled. Sparks drift slowly backward along the motion
 * vector plus a small perpendicular jitter, so they appear to flake off
 * the back of the moving thing.
 */
function emitSparks(
  em: SparkEmitter,
  wx: number, wy: number,
  vx: number, vy: number,
  color: string,
  spawnDist: number,
  size: number,
  duration: number,
  now: number,
): void {
  if (!em.seen) {
    em.prevX = wx;
    em.prevY = wy;
    em.seen = true;
    return;
  }
  const dx = wx - em.prevX;
  const dy = wy - em.prevY;
  em.prevX = wx;
  em.prevY = wy;
  const dist = Math.hypot(dx, dy);
  // Reset on teleport.
  if (dist > 60) { em.acc = 0; return; }
  em.acc += dist;

  const speed = Math.hypot(vx, vy);
  if (speed < 0.2) return;
  const ux = vx / speed;
  const uy = vy / speed;
  // Spawn-position jitter scales with the layer's spacing — bigger
  // emitters get bigger spawn-spread so the layers look distinct.
  const spawnSpreadPerp = spawnDist * 0.45;
  const spawnSpreadAlong = spawnDist * 0.55;

  while (em.acc >= spawnDist) {
    em.acc -= spawnDist;
    // Jitter the spawn position both along the motion vector (breaks
    // the strict uniform spacing) and perpendicular to it (breaks the
    // straight-line dot pattern). The result reads as a smoky wake
    // rather than a stippled stripe.
    const offAlong = (Math.random() - 0.5) * spawnSpreadAlong;
    const offPerp = (Math.random() - 0.5) * spawnSpreadPerp;
    const spawnWx = wx + ux * offAlong + (-uy) * offPerp;
    const spawnWy = wy + uy * offAlong + ux * offPerp;
    // Wider velocity ranges so some particles drift, others fly.
    const back = 0.2 + Math.random() * 1.0;
    const perp = (Math.random() - 0.5) * 2.2;
    effects.push({
      kind: 'particle',
      t0: now,
      // Duration 50%-140% of base — mixes quick flashes with long-fade tails.
      duration: duration * (0.5 + Math.random() * 0.9),
      wx: spawnWx,
      wy: spawnWy,
      vx: -ux * back + -uy * perp,
      vy: -uy * back + ux * perp,
      color,
      // Size 60%-160% of base — chunky mixed with fine motes.
      size: size * (0.6 + Math.random() * 1.0),
    });
  }
}

function detectStateChanges(s: GameState, now: number): void {
  // Mark when a serve starts (transition into 'play' from non-play).
  if (s.phase === 'play' && lastPlayPhase !== 'play') {
    lastServeStart = now;
  }
  lastPlayPhase = s.phase;
}

function hitPulse(whose: 'host' | 'guest', now: number): number {
  const age = now - (whose === 'host' ? lastHostHitAt : lastGuestHitAt);
  if (age < 0 || age > 280) return 0;
  return 1 - age / 280; // 1 right after hit, fades to 0
}

function computeShake(now: number): { dx: number; dy: number } {
  if (now >= shakeEndAt) return { dx: 0, dy: 0 };
  const remaining = (shakeEndAt - now) / 380;
  const mag = shakeMag * remaining;
  return {
    dx: (Math.random() - 0.5) * 2 * mag,
    dy: (Math.random() - 0.5) * 2 * mag,
  };
}

/** Idle pulse factor in [0,1] for subtle continuous animation. */
function idlePulse(now: number, periodMs = 2400): number {
  return 0.5 + 0.5 * Math.sin((now / periodMs) * Math.PI * 2);
}

/** Canvas-2D path. Renderer-router calls this when WebGL is disabled. */
export function render(
  canvas: HTMLCanvasElement,
  state: GameState,
  role: Role,
  now: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  renderInternal(ctx, { state, role }, now);
}

/** Throttle for ambient goal sparks. */
let lastGoalSparkAt = 0;
const GOAL_SPARK_INTERVAL_MS = 55;
/** Throttle for ambient rim sparks. */
let lastRimSparkAt = 0;
const RIM_SPARK_INTERVAL_MS = 45;
/** Throttle for grid intersection sparkles. */
let lastGridSparkAt = 0;
const GRID_SPARK_INTERVAL_MS = 90;

function emitGridSparkle(now: number): void {
  // Pick a random grid intersection (matching the inset used by both
  // renderers' grid layouts). Spawn 2-3 tiny sparks fanning outward.
  const margin = 30;
  const cols = 16, rows = 10;
  const i = Math.floor(Math.random() * (cols + 1));
  const j = Math.floor(Math.random() * (rows + 1));
  const wx = margin + (i / cols) * (RINK.width - 2 * margin);
  const wy = margin + (j / rows) * (RINK.height - 2 * margin);
  const roll = Math.random();
  const color = roll < 0.7 ? '#ffffff'
    : roll < 0.85 ? '#22e7ff' : '#ff2bd6';
  const count = 2 + Math.floor(Math.random() * 2);
  for (let k = 0; k < count; k++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 0.6 + Math.random() * 1.6;
    effects.push({
      kind: 'particle',
      t0: now,
      duration: 380 + Math.random() * 260,
      wx, wy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      color,
      size: 0.55 + Math.random() * 0.45,
    });
  }
}

function emitRimSpark(now: number): void {
  // Pick a random spot along the rim perimeter (excluding goal gaps),
  // emit a small pink spark drifting INWARD.
  const yLow = RINK.height / 2 - currentConfig.goalHalfHeight;
  const yHigh = RINK.height / 2 + currentConfig.goalHalfHeight;
  const margin = 20;
  let wx: number, wy: number, vx: number, vy: number;
  const r = Math.random();
  if (r < 0.42) {
    // Long side wy=0 (one lateral edge)
    wx = margin + Math.random() * (RINK.width - margin * 2);
    wy = 0;
    vx = (Math.random() - 0.5) * 1.5;
    vy = 0.8 + Math.random() * 1.4;
  } else if (r < 0.84) {
    // Long side wy=H
    wx = margin + Math.random() * (RINK.width - margin * 2);
    wy = RINK.height;
    vx = (Math.random() - 0.5) * 1.5;
    vy = -(0.8 + Math.random() * 1.4);
  } else if (r < 0.92) {
    // Short side wx=0, excluding goal opening
    wx = 0;
    wy = Math.random() < 0.5
      ? margin + Math.random() * (yLow - margin)
      : yHigh + Math.random() * (RINK.height - margin - yHigh);
    vx = 0.8 + Math.random() * 1.4;
    vy = (Math.random() - 0.5) * 1.5;
  } else {
    // Short side wx=W
    wx = RINK.width;
    wy = Math.random() < 0.5
      ? margin + Math.random() * (yLow - margin)
      : yHigh + Math.random() * (RINK.height - margin - yHigh);
    vx = -(0.8 + Math.random() * 1.4);
    vy = (Math.random() - 0.5) * 1.5;
  }
  effects.push({
    kind: 'particle',
    t0: now,
    duration: 500 + Math.random() * 400,
    wx, wy, vx, vy,
    color: COLORS.hostPaddle, // rim is pink; matches the existing rim color
    size: 0.6 + Math.random() * 0.5,
  });
}

function emitGoalSpark(goalX: number, color: string, now: number): void {
  const yLow = RINK.height / 2 - currentConfig.goalHalfHeight;
  const yHigh = RINK.height / 2 + currentConfig.goalHalfHeight;
  const wy = yLow + Math.random() * (yHigh - yLow);
  // Drift INWARD from the goal mouth — sparks fly into the rink so
  // they read as energy radiating from the goal toward the play area.
  const inwardSign = goalX < RINK.width / 2 ? 1 : -1;
  const vx = inwardSign * (1 + Math.random() * 2.5);
  const vy = (Math.random() - 0.5) * 1.4;
  effects.push({
    kind: 'particle',
    t0: now,
    duration: 700 + Math.random() * 400,
    wx: goalX,
    wy,
    vx,
    vy,
    color,
    size: 0.7 + Math.random() * 0.6,
  });
}

/**
 * Per-frame scene housekeeping shared between renderers. Both Canvas-2D
 * and WebGL paths call this BEFORE drawing — it ages the effect list,
 * spawns sparks from puck/paddle motion, tracks score/phase changes for
 * UI bumps, etc. Renderers just consume the resulting state.
 */
export function tickScene(state: GameState, now: number): void {
  // Track the active match config so render helpers can read per-match
  // sizes (paddle/puck radius) without us threading state through every
  // draw call. Set once per frame.
  currentConfig = state.config;
  currentPuckSpeed = Math.hypot(state.puck.vel.x, state.puck.vel.y);
  detectStateChanges(state, now);
  tickEffects(now);
  tickGridForces(now);
  // Ambient sparks rising from the goals — adds life regardless of phase.
  if (now - lastGoalSparkAt > GOAL_SPARK_INTERVAL_MS) {
    lastGoalSparkAt = now;
    emitGoalSpark(0, COLORS.hostPaddle, now);
    emitGoalSpark(RINK.width, COLORS.guestPaddle, now);
  }
  // Ambient sparks emitted from random points along the rim.
  if (now - lastRimSparkAt > RIM_SPARK_INTERVAL_MS) {
    lastRimSparkAt = now;
    emitRimSpark(now);
  }
  // Random sparkles at grid intersections — tiny ambient shimmer.
  if (now - lastGridSparkAt > GRID_SPARK_INTERVAL_MS) {
    lastGridSparkAt = now;
    emitGridSparkle(now);
  }
  if (state.phase === 'play') {
    // THREE-layer puck trail for chunky comet-like wake. Spaced apart
    // enough that the three layers each have distinct cadences and the
    // ribbon doesn't just collapse into a single bright line.
    // Layer 1: dense bright core (the bright streak you read first).
    emitSparks(
      puckEmitter,
      state.puck.pos.x, state.puck.pos.y,
      state.puck.vel.x, state.puck.vel.y,
      COLORS.puck, 3, 2.6, 700,
      now,
    );
    // Layer 2: glow ribbon — bigger, longer-lived, slightly faded.
    emitSparks(
      puckGlowEmitter,
      state.puck.pos.x, state.puck.pos.y,
      state.puck.vel.x, state.puck.vel.y,
      COLORS.puckGlow, 8, 5.0, 1100,
      now,
    );
    // Layer 3: ambient halo — huge soft particles that linger far back
    // behind the puck. Sparse spacing so they don't crowd the core,
    // long duration so the wake extends for several puck-diameters.
    emitSparks(
      puckHaloEmitter,
      state.puck.pos.x, state.puck.pos.y,
      state.puck.vel.x, state.puck.vel.y,
      COLORS.puckGlow, 24, 8.5, 1700,
      now,
    );
    const hVx = state.host.pos.x - state.host.prev.x;
    const hVy = state.host.pos.y - state.host.prev.y;
    if (Math.hypot(hVx, hVy) > 3) {
      emitSparks(
        hostEmitter,
        state.host.pos.x, state.host.pos.y,
        hVx, hVy,
        COLORS.hostPaddle, 10, 1.4, 280,
        now,
      );
    }
    const gVx = state.guest.pos.x - state.guest.prev.x;
    const gVy = state.guest.pos.y - state.guest.prev.y;
    if (Math.hypot(gVx, gVy) > 3) {
      emitSparks(
        guestEmitter,
        state.guest.pos.x, state.guest.pos.y,
        gVx, gVy,
        COLORS.guestPaddle, 10, 1.4, 280,
        now,
      );
    }
  } else {
    resetEmitters();
  }
}

function renderInternal(ctx: CanvasRenderingContext2D, rs: RenderState, now: number): void {
  const view = computeView(ctx.canvas, rs.role);
  tickScene(rs.state, now);

  // Game-world rendering, with screen shake applied. UI overlays (score
  // and banner) render OUTSIDE the shake transform.
  const shake = computeShake(now);

  ctx.save();
  if (shake.dx !== 0 || shake.dy !== 0) ctx.translate(shake.dx, shake.dy);

  // Background (the static cache is now transparent outside the rink so
  // the WebGL renderer can put stars behind it; Canvas-2D fills bg here).
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // Static layer: lines + rim + pockets (no bg fill on its own).
  const cache = ensureStaticLayer(ctx.canvas, rs.role);
  ctx.drawImage(cache, 0, 0);

  // Deformable grid background, drawn UNDER goal celebration / entities
  // but ON TOP of the bg + rim cache. Clipped to the rink trapezoid so
  // grid lines don't extend past the rim.
  drawGrid(ctx, view, now);

  // Goal-celebration overlays (under entities).
  drawGoalCelebration(ctx, view, now);

  // Wall flashes — drawn on the rim, beneath entities.
  drawWallFlashes(ctx, view, now);

  // (Puck trail is emitted as 'particle' effects above and drawn later
  // by drawParticles alongside the goal burst — same render path.)

  // Update entities + sort back-to-front.
  hostPaddleEnt.wx = rs.state.host.pos.x;
  hostPaddleEnt.wy = rs.state.host.pos.y;
  hostPaddleEnt.depthT = project(hostPaddleEnt.wx, hostPaddleEnt.wy, view).depthT;
  guestPaddleEnt.wx = rs.state.guest.pos.x;
  guestPaddleEnt.wy = rs.state.guest.pos.y;
  guestPaddleEnt.depthT = project(guestPaddleEnt.wx, guestPaddleEnt.wy, view).depthT;
  puckEnt.wx = rs.state.puck.pos.x;
  puckEnt.wy = rs.state.puck.pos.y;
  puckEnt.depthT = project(puckEnt.wx, puckEnt.wy, view).depthT;
  let a: RenderEntity = hostPaddleEnt;
  let b: RenderEntity = puckEnt;
  let c: RenderEntity = guestPaddleEnt;
  let tmp: RenderEntity;
  if (a.depthT < b.depthT) { tmp = a; a = b; b = tmp; }
  if (b.depthT < c.depthT) { tmp = b; b = c; c = tmp; }
  if (a.depthT < b.depthT) { tmp = a; a = b; b = tmp; }
  // Hide the puck during goal phase — it just "exploded".
  const puckGone = rs.state.phase === 'goal-host' || rs.state.phase === 'goal-guest';
  if (!puckGone || a.kind !== 'puck') drawEntity(ctx, a, view, now);
  if (!puckGone || b.kind !== 'puck') drawEntity(ctx, b, view, now);
  if (!puckGone || c.kind !== 'puck') drawEntity(ctx, c, view, now);

  // Impact rings + particles on top of entities.
  drawImpactRings(ctx, view, now);
  drawParticles(ctx, view, now);

  ctx.restore(); // end shake

  // Score number and phase banner are rendered as HTML overlays (see
  // src/game/hud.ts) so both backends display them identically.
}

/** Glowing vertical line along each back wall — drawn in full-wall
 *  goal modes (e.g., Pong) so the player can see the entire wall is
 *  the goal. Sits inside the chamfered y-range so it doesn't intersect
 *  the rim corners. */
function drawFullWallGoalLine(ctx: CanvasRenderingContext2D, v: View): void {
  const R = RINK.cornerRadius;
  const yA = R;
  const yB = RINK.height - R;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineWidth = 5;

  // Host's goal at wx=0 — paint in host's color.
  ctx.strokeStyle = COLORS.hostPaddle;
  ctx.shadowColor = COLORS.hostPaddle;
  ctx.shadowBlur = 24;
  let p1 = project(0, yA, v);
  let p2 = project(0, yB, v);
  ctx.beginPath();
  ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p2.x, p2.y);
  ctx.stroke();

  // Guest's goal at wx=W — paint in guest's color.
  ctx.strokeStyle = COLORS.guestPaddle;
  ctx.shadowColor = COLORS.guestPaddle;
  p1 = project(RINK.width, yA, v);
  p2 = project(RINK.width, yB, v);
  ctx.beginPath();
  ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p2.x, p2.y);
  ctx.stroke();
  ctx.restore();
}

/**
 * Trace the rink perimeter (4 walls + 4 rounded corners) in world coords,
 * projecting each vertex to screen. Arcs are approximated by sampling
 * `ARC_STEPS` segments per 90°, which projects accurately under
 * perspective. When `openGoals` is true, the path is emitted as two
 * sub-paths broken at the goal mouths (the gaps are the goal openings).
 */
const ARC_STEPS = 10;

function traceRinkPath(
  ctx: CanvasRenderingContext2D,
  v: View,
  openGoals: boolean,
): void {
  const W = RINK.width;
  const H = RINK.height;
  const R = RINK.cornerRadius;
  const yLow = H / 2 - currentConfig.goalHalfHeight;
  const yHigh = H / 2 + currentConfig.goalHalfHeight;

  const lt = (wx: number, wy: number): void => {
    const p = project(wx, wy, v);
    ctx.lineTo(p.x, p.y);
  };
  const mt = (wx: number, wy: number): void => {
    const p = project(wx, wy, v);
    ctx.moveTo(p.x, p.y);
  };
  // Sample a quarter-arc centered at (acx, acy), sweeping from a0 to a1
  // (radians, CCW), projecting each sample to screen and emitting lineTo.
  const arcTo = (acx: number, acy: number, a0: number, a1: number): void => {
    for (let i = 1; i <= ARC_STEPS; i++) {
      const t = i / ARC_STEPS;
      const a = a0 + (a1 - a0) * t;
      lt(acx + R * Math.cos(a), acy + R * Math.sin(a));
    }
  };

  if (openGoals) {
    // Half 1 (wy=0 side): near-left wall → NW arc → top wall → NE arc
    // → far-left wall. Gap at the near goal opening.
    mt(0, yLow);
    lt(0, R);
    arcTo(R, R, Math.PI, 1.5 * Math.PI);          // NW arc → (R, 0)
    lt(W - R, 0);
    arcTo(W - R, R, 1.5 * Math.PI, 2 * Math.PI);  // NE arc → (W, R)
    lt(W, yLow);
    // (gap at far-side goal opening)
    mt(W, yHigh);
    lt(W, H - R);
    arcTo(W - R, H - R, 0, 0.5 * Math.PI);        // SE arc → (W-R, H)
    lt(R, H);
    arcTo(R, H - R, 0.5 * Math.PI, Math.PI);      // SW arc → (0, H-R)
    lt(0, yHigh);
    // (gap at near-side goal opening closes the loop visually)
  } else {
    mt(R, 0);
    lt(W - R, 0);
    arcTo(W - R, R, 1.5 * Math.PI, 2 * Math.PI);  // NE
    lt(W, H - R);
    arcTo(W - R, H - R, 0, 0.5 * Math.PI);        // SE
    lt(R, H);
    arcTo(R, H - R, 0.5 * Math.PI, Math.PI);      // SW
    lt(0, R);
    arcTo(R, R, Math.PI, 1.5 * Math.PI);          // NW
    ctx.closePath();
  }
}

function drawTableSurface(ctx: CanvasRenderingContext2D, v: View): void {
  // Vertical gradient for depth cue (brighter near, dimmer far).
  const grad = ctx.createLinearGradient(0, v.nearY, 0, v.farY);
  grad.addColorStop(0, COLORS.surface);
  grad.addColorStop(1, COLORS.surfaceFar);
  ctx.fillStyle = grad;

  ctx.beginPath();
  traceRinkPath(ctx, v, false);
  ctx.fill();
}

function drawCenterLine(ctx: CanvasRenderingContext2D, v: View): void {
  const p1 = project(RINK.width / 2, 0, v);
  const p2 = project(RINK.width / 2, RINK.height, v);
  ctx.save();
  ctx.strokeStyle = COLORS.line;
  ctx.shadowColor = COLORS.lineGlow;
  ctx.shadowBlur = 14;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p2.x, p2.y);
  ctx.stroke();
  ctx.restore();
}

function drawCenterCircle(ctx: CanvasRenderingContext2D, v: View): void {
  const p = project(RINK.width / 2, RINK.height / 2, v);
  ctx.save();
  ctx.strokeStyle = COLORS.line;
  ctx.shadowColor = COLORS.lineGlow;
  ctx.shadowBlur = 14;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(p.x, p.y, 60 * p.latScale, 60 * p.depthScale, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawRim(ctx: CanvasRenderingContext2D, v: View): void {
  const gh = currentConfig.goalHalfHeight;
  const yLow = RINK.height / 2 - gh;
  const yHigh = RINK.height / 2 + gh;

  // Goal pockets — drawn FIRST so the rim line and goal posts paint over
  // the pocket edges where they meet. Visual depth matches the physics
  // chamber (RINK.pocketDepth): puck enters through the mouth, bounces
  // off the pocket sides, and only scores when it crosses the back.
  drawGoalPocket(ctx, 0, -currentConfig.pocketDepth, yLow, yHigh, COLORS.hostPaddle, v);
  drawGoalPocket(ctx, RINK.width, RINK.width + currentConfig.pocketDepth, yLow, yHigh, COLORS.guestPaddle, v);

  // Full-wall goal indicator: when goalHalfHeight ≈ half the rink (Pong
  // mode), there's no rim wall on the left/right — the entire back wall
  // is the goal. Draw a bright glowing line along each back wall so the
  // player can see the goal extends the full height.
  if (currentConfig.goalHalfHeight >= RINK.height / 2 - 1) {
    drawFullWallGoalLine(ctx, v);
  }

  // Octagonal rim — 4 walls + 4 chamfered corners — drawn as two sub-paths
  // broken at the goal mouths. Single beginPath/stroke = one shadowBlur pass.
  ctx.save();
  ctx.strokeStyle = COLORS.rim;
  ctx.shadowColor = COLORS.rimGlow;
  ctx.shadowBlur = 22;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.beginPath();
  traceRinkPath(ctx, v, true);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw a goal pocket as a trapezoid extending past the rink end in the
 * defender's color. `wxInner` is the rim edge (wx=0 or wx=RINK.width);
 * `wxOuter` is the world x just past it (negative for host's goal,
 * past-RINK.width for guest's goal). The y range is the goal opening.
 */
function drawGoalPocket(
  ctx: CanvasRenderingContext2D,
  wxInner: number, wxOuter: number,
  wyLow: number, wyHigh: number,
  color: string,
  v: View,
): void {
  const inLo = project(wxInner, wyLow, v);
  const inHi = project(wxInner, wyHigh, v);
  const outHi = project(wxOuter, wyHigh, v);
  const outLo = project(wxOuter, wyLow, v);

  ctx.save();
  // Translucent fill so the pocket looks like a recessed cavity.
  ctx.fillStyle = color + '55'; // ~33% alpha (RGBA hex)
  ctx.beginPath();
  ctx.moveTo(inLo.x, inLo.y);
  ctx.lineTo(inHi.x, inHi.y);
  ctx.lineTo(outHi.x, outHi.y);
  ctx.lineTo(outLo.x, outLo.y);
  ctx.closePath();
  ctx.fill();

  // Bright outline on the outer + side walls of the pocket (skip the
  // inner edge — that's where the rim sits / the puck enters).
  ctx.strokeStyle = color;
  ctx.shadowColor = color;
  ctx.shadowBlur = 18;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(inLo.x, inLo.y);
  ctx.lineTo(outLo.x, outLo.y);
  ctx.lineTo(outHi.x, outHi.y);
  ctx.lineTo(inHi.x, inHi.y);
  ctx.stroke();
  ctx.restore();
}

function drawPaddle(
  ctx: CanvasRenderingContext2D,
  wx: number, wy: number,
  color: string,
  v: View,
  pulse: number,    // 0..1 — recent-hit intensity
  now: number,
): void {
  const sprites = ensureEntitySprites();
  const sprite = color === COLORS.hostPaddle ? sprites.host : sprites.guest;
  const p = project(wx, wy, v);
  // Subtle continuous idle pulse + a punchier hit pulse layered on top.
  const idle = idlePulse(now);
  const sizeBoost = 1 + 0.04 * idle + 0.20 * pulse;
  // rx (screen-x extent) tracks the LATERAL world half-extent — wide
  // when paddleHalfHeight > paddleRadius (Pong-style horizontal bar).
  // ry (screen-y extent) tracks the DEPTH world half-extent — thin.
  // Both use latScale so circle paddles remain visually circular (not
  // depth-foreshortened to ellipses).
  const rx = currentConfig.paddleHalfHeight * p.latScale * sizeBoost;
  const ry = currentConfig.paddleRadius * p.latScale * sizeBoost * ENTITY_FORESHORTEN;
  const paddleStretch = currentConfig.paddleHalfHeight / currentConfig.paddleRadius;

  ctx.save();

  // Cast shadow on the table (darker ellipse under the paddle).
  ctx.fillStyle = COLORS.shadow;
  ctx.beginPath();
  ctx.ellipse(p.x, p.y + ry * 0.18, rx * 1.05, ry * 0.85, 0, 0, Math.PI * 2);
  ctx.fill();

  // Pill paddles (Pong) render as a sharp-cornered rectangle with a
  // shadowBlur halo, instead of the stretched-circle sprite. Looks
  // distinctly like a Pong bar instead of an oval.
  const isRect = paddleStretch > 1.4;
  if (isRect) {
    ctx.shadowColor = color;
    ctx.shadowBlur = Math.max(8, rx * 1.6);
    ctx.fillStyle = color;
    ctx.fillRect(p.x - rx, p.y - ry, rx * 2, ry * 2);
    ctx.shadowBlur = 0;
    // Brighter inner core for crispness.
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.35;
    ctx.fillRect(p.x - rx * 0.55, p.y - ry * 0.92, rx * 1.1, ry * 1.84);
    ctx.globalAlpha = 1;

    if (pulse > 0.05) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = pulse * 0.6;
      ctx.fillStyle = color;
      const ps = 1 + pulse * 0.3;
      ctx.fillRect(p.x - rx * ps, p.y - ry * ps, rx * 2 * ps, ry * 2 * ps);
    }
    ctx.restore();
    return;
  }

  // Sprite path (circle paddle). Sprite covers body + halo.
  const dw = rx * 2 * SPRITE_DRAW_RATIO;
  const dh = ry * 2 * SPRITE_DRAW_RATIO;
  // Body + glow + cap (all baked into the sprite, one drawImage).
  ctx.drawImage(sprite, p.x - dw / 2, p.y - dh / 2, dw, dh);

  // Hit pulse: additive overlay for punch on contact.
  if (pulse > 0.05) {
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = pulse * 0.7;
    const ps = 1 + pulse * 0.4;
    ctx.drawImage(
      sprite,
      p.x - (dw * ps) / 2,
      p.y - (dh * ps) / 2,
      dw * ps,
      dh * ps,
    );
  }

  ctx.restore();
}

function drawPuck(
  ctx: CanvasRenderingContext2D,
  wx: number, wy: number,
  v: View,
  now: number,
): void {
  const p = project(wx, wy, v);

  // Serve drop-in: the puck materializes for ~360ms after a serve starts.
  // During this window it's larger and translucent, settling into normal.
  const serveAge = now - lastServeStart;
  const dropping = serveAge >= 0 && serveAge < 360 && lastServeStart > 0;
  const dropT = dropping ? serveAge / 360 : 1;
  const scale = dropping ? 1 + (1 - dropT) * 1.5 : 1;
  const alpha = dropping ? Math.min(1, dropT * 2) : 1;

  const rx = currentConfig.puckRadius * p.latScale * scale;
  const ry = rx * ENTITY_FORESHORTEN;

  const sprites = ensureEntitySprites();
  const dw = rx * 2 * SPRITE_DRAW_RATIO;
  const dh = ry * 2 * SPRITE_DRAW_RATIO;

  ctx.save();

  // Tight shadow just under the puck (only when fully materialized).
  if (alpha > 0.7) {
    ctx.fillStyle = COLORS.shadow;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + ry * 0.15, rx * 1.1, ry * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalAlpha = alpha;
  ctx.drawImage(sprites.puck, p.x - dw / 2, p.y - dh / 2, dw, dh);

  // Speed-scaled glow overlay: an additive pass with a bigger sprite +
  // alpha proportional to current puck speed. Reads as "the puck is
  // radiating more energy the faster it moves." Only kicks in above a
  // small threshold so the puck isn't ringed when crawling.
  const speedT = Math.min(1, currentPuckSpeed / 14);
  if (alpha > 0.7 && speedT > 0.15) {
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = speedT * 0.55;
    const ps = 1 + speedT * 0.55;
    ctx.drawImage(
      sprites.puck,
      p.x - (dw * ps) / 2,
      p.y - (dh * ps) / 2,
      dw * ps,
      dh * ps,
    );
  }

  // Extra bloom during the serve drop-in materialization.
  if (dropping) {
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = alpha * (1 - dropT) * 0.6;
    const ps = 1 + (1 - dropT) * 0.4;
    ctx.drawImage(
      sprites.puck,
      p.x - (dw * ps) / 2,
      p.y - (dh * ps) / 2,
      dw * ps,
      dh * ps,
    );
  }

  ctx.restore();
}

// ─── Effect renderers ─────────────────────────────────────────────────────

/**
 * Geometry-Wars-style deformable grid. World-space points on a uniform
 * rectangular grid are displaced under active force fields, then
 * projected and connected by lines. Clipped to the rink interior so
 * lines don't bleed past the rim.
 */
// Fixed cell count rather than fixed spacing guarantees the boundary
// lines at wy=0/wy=H and wx=0/wx=W are always present, so the grid
// reaches the rim on all four edges. Picked so cells are roughly square
// in world units: 800/16 = 50, 500/10 = 50.
const GRID_COLS = 16;
const GRID_ROWS = 10;
const GRID_PTS = (GRID_COLS + 1) * (GRID_ROWS + 1);
// Reusable typed-array buffers for projected grid positions. Avoids
// per-frame allocation and lets the path be built twice (once for the
// halo stroke, once for the core) without recomputing projections.
const gridSX = new Float32Array(GRID_PTS);
const gridSY = new Float32Array(GRID_PTS);

function drawGrid(ctx: CanvasRenderingContext2D, v: View, now: number): void {
  ctx.save();
  // Rink clip path: octagon (matches the rim). Closed; goals are not
  // openings here because we still want the grid to fill the goal-mouth
  // strip — only the rim line and pockets shape the goal opening.
  ctx.beginPath();
  traceRinkPath(ctx, v, false);
  ctx.clip();

  // Faint table fill for contrast under the grid.
  ctx.fillStyle = '#08010c';
  ctx.fillRect(0, 0, v.canvasW, v.canvasH);

  // Pass 1: project every grid intersection once and cache it.
  const disp = { dx: 0, dy: 0 };
  const stride = GRID_COLS + 1;
  for (let j = 0; j <= GRID_ROWS; j++) {
    const wy = (j / GRID_ROWS) * RINK.height;
    for (let i = 0; i <= GRID_COLS; i++) {
      const wx = (i / GRID_COLS) * RINK.width;
      gridDisplacement(wx, wy, now, disp);
      const p = project(wx + disp.dx, wy + disp.dy, v);
      const idx = j * stride + i;
      gridSX[idx] = p.x;
      gridSY[idx] = p.y;
    }
  }

  // Build the full grid path once. Two stroke passes (halo + core) share it.
  ctx.beginPath();
  for (let j = 0; j <= GRID_ROWS; j++) {
    const base = j * stride;
    ctx.moveTo(gridSX[base]!, gridSY[base]!);
    for (let i = 1; i <= GRID_COLS; i++) {
      ctx.lineTo(gridSX[base + i]!, gridSY[base + i]!);
    }
  }
  for (let i = 0; i <= GRID_COLS; i++) {
    ctx.moveTo(gridSX[i]!, gridSY[i]!);
    for (let j = 1; j <= GRID_ROWS; j++) {
      const idx = j * stride + i;
      ctx.lineTo(gridSX[idx]!, gridSY[idx]!);
    }
  }

  // Halo pass: wide, low-alpha cyan acts as the bloom envelope.
  ctx.strokeStyle = '#22e7ff44';
  ctx.lineWidth = 3;
  ctx.stroke();
  // Core pass: bright thin cyan on top of the halo.
  ctx.strokeStyle = '#22e7ffcc';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.restore();
}

// Comet-style trail removed in favor of streaming-spark particles
// emitted by emitPuckSparks() and rendered through drawParticles().

function drawImpactRings(ctx: CanvasRenderingContext2D, v: View, now: number): void {
  ctx.save();
  ctx.lineCap = 'round';
  for (const e of effects) {
    if (e.kind !== 'impact') continue;
    const t = (now - e.t0) / e.duration;
    if (t < 0 || t > 1) continue;
    const p = project(e.wx, e.wy, v);
    const baseR = (currentConfig.paddleRadius + currentConfig.puckRadius) * p.latScale;
    const r = baseR * (0.6 + t * 1.3);
    const alpha = 1 - t;
    ctx.strokeStyle = e.color;
    // Halo: wide low-alpha ring.
    ctx.globalAlpha = alpha * 0.45;
    ctx.lineWidth = 9 * (1 - t * 0.5);
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, r, r * ENTITY_FORESHORTEN, 0, 0, Math.PI * 2);
    ctx.stroke();
    // Core: thin opaque ring on top.
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 2.5 * (1 - t * 0.6);
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, r, r * ENTITY_FORESHORTEN, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function drawWallFlashes(ctx: CanvasRenderingContext2D, v: View, now: number): void {
  ctx.save();
  for (const e of effects) {
    if (e.kind !== 'wallflash') continue;
    const t = (now - e.t0) / e.duration;
    if (t < 0 || t > 1) continue;
    const p = project(e.wx, e.wy, v);
    const r = (currentConfig.puckRadius * 1.4) * p.latScale * (1 + t * 0.6);
    const alpha = 1 - t;
    ctx.fillStyle = '#fff7a8';
    // Halo.
    ctx.globalAlpha = alpha * 0.30;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, r * 2, r * ENTITY_FORESHORTEN * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
    // Core.
    ctx.globalAlpha = alpha * 0.85;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, r, r * ENTITY_FORESHORTEN * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawGoalCelebration(ctx: CanvasRenderingContext2D, v: View, now: number): void {
  for (const e of effects) {
    if (e.kind !== 'goal') continue;
    const t = (now - e.t0) / e.duration;
    if (t < 0 || t > 1) continue;
    const pulse = Math.max(0, Math.cos(t * Math.PI * 3) * Math.exp(-t * 1.5));
    const color = e.scorer === 'host' ? COLORS.hostPaddle : COLORS.guestPaddle;
    const concededAtWX = e.scorer === 'host' ? RINK.width : 0;
    const concededOuterWX = e.scorer === 'host' ? RINK.width + 36 : -36;
    const yLow = RINK.height / 2 - currentConfig.goalHalfHeight;
    const yHigh = RINK.height / 2 + currentConfig.goalHalfHeight;
    const inLo = project(concededAtWX, yLow, v);
    const inHi = project(concededAtWX, yHigh, v);
    const outHi = project(concededOuterWX, yHigh, v);
    const outLo = project(concededOuterWX, yLow, v);
    ctx.save();
    // Single bright fill — no shadowBlur. The grid distortion + particle
    // burst around this already provide the "glow" reading.
    ctx.globalAlpha = 0.6 + 0.4 * pulse;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(inLo.x, inLo.y);
    ctx.lineTo(inHi.x, inHi.y);
    ctx.lineTo(outHi.x, outHi.y);
    ctx.lineTo(outLo.x, outLo.y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}

/**
 * Particle renderer. Drops the per-stroke shadowBlur (was the bottleneck
 * for high counts) and simulates the glow with a two-pass thick+thin
 * stroke per particle, blended additively. Scales to 100+ particles per
 * frame at 60Hz.
 */
function drawParticles(ctx: CanvasRenderingContext2D, v: View, now: number): void {
  ctx.save();
  ctx.lineCap = 'round';
  // Additive blending — overlapping particles brighten naturally, no
  // alpha-compositing dimness. Plays well with the dark grid background.
  ctx.globalCompositeOperation = 'lighter';

  let curColor = '';
  for (const e of effects) {
    if (e.kind !== 'particle') continue;
    const ageMs = now - e.t0;
    const t = ageMs / e.duration;
    if (t < 0 || t > 1) continue;

    const ageTicks = ageMs / 16.67;
    const decay = Math.pow(0.985, ageTicks);
    const wx = e.wx + e.vx * ageTicks * decay;
    const wy = e.wy + e.vy * ageTicks * decay;
    const vxNow = e.vx * decay;
    const vyNow = e.vy * decay;
    const speed = Math.hypot(vxNow, vyNow);
    const streakLen = Math.min(20, 3 + speed * 1.4);
    const invSpeed = 1 / Math.max(speed, 0.01);
    const wxTail = wx - vxNow * invSpeed * streakLen;
    const wyTail = wy - vyNow * invSpeed * streakLen;
    const pHead = project(wx, wy, v);
    const pTail = project(wxTail, wyTail, v);

    if (e.color !== curColor) {
      curColor = e.color;
      ctx.strokeStyle = e.color;
    }
    const alpha = (1 - t);
    const coreWidth = Math.max(0.5, e.size * pHead.latScale * 0.4 * (1 - t * 0.5));

    // Bloom (wide, low alpha) — fakes the glow halo.
    ctx.globalAlpha = alpha * 0.35;
    ctx.lineWidth = coreWidth * 3.2;
    ctx.beginPath();
    ctx.moveTo(pTail.x, pTail.y);
    ctx.lineTo(pHead.x, pHead.y);
    ctx.stroke();

    // Core (thin, bright).
    ctx.globalAlpha = alpha * 0.95;
    ctx.lineWidth = coreWidth;
    ctx.beginPath();
    ctx.moveTo(pTail.x, pTail.y);
    ctx.lineTo(pHead.x, pHead.y);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Map a pointer event's CSS coordinates onto rink (world) coordinates,
 * applying the per-role inverse of the POV projection.
 */
export function screenToRink(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  role: Role,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const dpr = canvas.width / rect.width;
  const sx = (clientX - rect.left) * dpr;
  const sy = (clientY - rect.top) * dpr;
  return unproject(sx, sy, computeView(canvas, role));
}
